import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useDeleteLocation } from '@/hooks/useLocations'

interface DeleteLocationDialogProps {
  /** Whether dialog is open */
  open: boolean
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void
  /** ID of location to delete */
  locationId: string
  /** Name of location (for display in confirmation message) */
  locationName: string
}

/**
 * Confirmation dialog for deleting a location
 * Performs soft delete (sets deleted_at timestamp)
 */
export function DeleteLocationDialog({
  open,
  onOpenChange,
  locationId,
  locationName,
}: DeleteLocationDialogProps) {
  const deleteMutation = useDeleteLocation()

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(locationId)
      onOpenChange(false)
    } catch {
      // Error handling is done in the mutation hook
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Location</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete <strong>"{locationName}"</strong>?
            <br />
            <br />
            This will also remove all associated hangars and aircraft positions.
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Location'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
